import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {NavLink} from "react-router-dom";
import {styled} from "@mui/system";
import {useAuth} from "hooks/useAuth";
import Icon from "shared/ui/Icon";
import ScrollBar from "shared/ui/ScrollBar";
import {useEffect, useState} from "react";
import Tooltip from "@mui/material/Tooltip";

export interface IMenuItem {
    name: string;
    url: string;
    icon: string;
    type: number;
    target: string;
    enabled: number;
    extra?: string;
}

const MENU_ITEM_TYPE_HEADER = 0;
const MENU_ITEM_TYPE_LINK = 1;

export const NavLinkStyled = styled(NavLink)(({theme}) => ({
    color: theme.palette.secondary.light,
    textDecoration: "none",
    clear: "both",
    paddingRight: theme.spacing(2),
    "& svg": {
        color: theme.palette.primary.light,
    },
    "&:hover, &.active": {
        color: theme.palette.primary.main,
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
}));

const defaultMenuItems = [
    {
        name: "Autentificare",
        url: "/",
        icon: "fas user-lock",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    },
    {name: "Informatii", url: "", icon: "", type: MENU_ITEM_TYPE_HEADER, target: "_self", enabled: 1, extra: ""},
    {
        name: "Proiecte PNRR",
        url: "/pnrr",
        icon: "fas rectangle-vertical-history",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    },
    // {
    //     name: "Granturi PNRAS",
    //     url: "/pnras",
    //     icon: "fas folders",
    //     type: MENU_ITEM_TYPE_LINK,
    //     target: "_self",
    //     enabled: 1,
    //     extra: ""
    // },
    {
        name: "Tip cont",
        url: "/tip-cont",
        icon: "fas file-user",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    },
    {
        name: "Abonamente",
        url: "/abonamente",
        icon: "fas tags",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    },
    {
        name: "Instructiuni",
        url: "/instructiuni",
        icon: "fas gear",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    },
    {
        name: "Contact",
        url: "/contact-brio",
        icon: "fas location-dot",
        type: MENU_ITEM_TYPE_LINK,
        target: "_self",
        enabled: 1,
        extra: ""
    }
];

const renderMenuHeader = (item: any, open: boolean) => (
    <Box display="flex" alignItems="center" mt={2} key={item.name} height="22.4px">
        <Box
            sx={(theme) => ({
                height: theme.spacing(1.6),
            })}
            display="flex"
            justifyContent="flex-end"
            mr={1.5}
        >
            <Box
                sx={(theme) => ({
                    // width: theme.spacing(5.4),
                    [theme.breakpoints.up('xs')]: {
                        width: theme.spacing(3),
                        minWidth: theme.spacing(3),
                    },
                    [theme.breakpoints.up('md')]: {
                        width: theme.spacing(4),
                        minWidth: theme.spacing(4),
                    },
                    [theme.breakpoints.up('lg')]: {
                        width: theme.spacing(4.3),
                        minWidth: theme.spacing(4.3),
                    },
                    [theme.breakpoints.up('xl')]: {
                        width: theme.spacing(5.4),
                        minWidth: theme.spacing(5.4),
                    },
                    borderBottom: `solid 2px ${theme.palette.primary.light}`,
                })}
            />
        </Box>
        {open && (
            <Box flexGrow={1}>
                <Typography
                    variant="h2"
                    sx={{fontWeight: 300}}
                    color="text.disabled"
                    dangerouslySetInnerHTML={{__html: item.name}}
                />
            </Box>
        )}
    </Box>
);

const renderMenuLink = (item: any, index: any, small: boolean, open: boolean) => (
    <Box key={index}>
        <Tooltip
            title={<Typography variant="body1">{item.name}</Typography>}
            placement="right"
            disableHoverListener={open}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -30],
                            },
                        },
                    ],
                },
            }}
        >
            <NavLinkStyled to={item.url} target={item.target}>
                <Box component="span" display="inline-flex" alignItems="center" py={0.5}>
                    <Box
                        component="span"
                        sx={{
                            width: {xs: "30px", md: "40px", lg: "43px", xl: "54px"},
                            display: "flex",
                            justifyContent: "flex-end",
                            marginRight: {xs: 0.5, lg: 1, xl: 1.5},
                        }}
                    >
                        {item.icon && <Icon icon={item.icon} fixedWidth/>}
                    </Box>
                    {open && (
                        <Box component="span" flexGrow={1}>
                            <Typography variant={small ? "body2" : "body1"}>{item.name}</Typography>
                        </Box>
                    )}
                </Box>
            </NavLinkStyled>
        </Tooltip>

    </Box>
);

const Menu = ({direction = "vertical", open = true}: { direction?: "vertical" | "horizontally", open?: boolean }) => {
    const {user} = useAuth();
    const [menu, setMenu] = useState<IMenuItem[]>(defaultMenuItems);

    useEffect(() => {
        setMenu(user.menu || defaultMenuItems);
    }, [user]);

    return (
        <>
            {direction === "vertical" && (
                <ScrollBar style={{maxHeight: "100%"}} forceVisible="x" autoHide={false}>
                    <Box mb={3} sx={{overflowX: "hidden"}}>
                        {menu
                            .filter((item) => item.enabled)
                            .map((item, index) =>
                                item.type === MENU_ITEM_TYPE_HEADER
                                    ? renderMenuHeader(item, open)
                                    : renderMenuLink(item, index, false, open)
                            )}
                    </Box>
                </ScrollBar>
            )}
            {direction === "horizontally" && (
                <Box display={{xs: "block", lg: "flex"}} flexDirection="row" height="100%" width="100%"
                     alignItems="space-between" justifyContent="space-evenly"
                     sx={{columns: {xs: 1, sm: 2, md: 3, lg: 1}}}>
                    {menu
                        .filter((item) => item.enabled)
                        .map((item, index) =>
                            item.type === MENU_ITEM_TYPE_HEADER
                                ? <Box key={`header-${index}`} display="none"/>
                                : renderMenuLink(item, index, true, open)
                        )}
                </Box>
            )}
        </>
    );
};

export default Menu;
